<script setup lang="ts">
import { toRefs, computed } from 'vue';
import CustomSlider from '@/components/common/CustomSlider.vue';

const emit = defineEmits(['blur', 'change', 'update:modelValue', 'close']);

const props = defineProps({
  modelValue: Boolean,
  width: { type: [Number, String], default: 500 },
  /** 不传时：progress 完全随内容；standard 使用 200px 作为 min-height */
  height: { type: [Number, String], default: undefined },
  title: String,
  top: { type: String, default: '30vh' },
  destroyOnClose: { type: Boolean, default: false },
  /** standard：默认带标题栏；progress：进度/加载样式（原 CustomTip isProgress），文案+滑块+默认插槽 */
  variant: { type: String, default: 'standard' },
  /** variant=progress 时主文案 */
  progressTip: { type: String, default: '' },
  /** variant=progress 时进度条 0–100 */
  progress: { type: Number, default: 0 },
  /** variant=progress 是否显示进度条 */
  showProgressBar: { type: Boolean, default: true },
  /** variant=progress 顶部图示：role | cloud | import */
  progressVisual: { type: String, default: 'role' },
  isShowClose: { type: Boolean, default: true },
  isSuspensionHeader: { type: Boolean, default: false },
  isDanger: { type: Boolean, default: false }, //是否危险弹窗
  isDark: { type: Boolean, default: false }, //是否黑色背景弹窗
  zIndex: { type: Number, default: 99999 },
  closeOnClickModal: { type: Boolean, default: false },
  transition: { type: [String, Object], default: 'dialog-fade' },
  appendToBody: { type: Boolean, default: false }, //Dialog 自身是否插入至 body 元素上。
  appendTo: { type: String, default: 'body' }, //	Dialog 挂载到哪个 DOM 元素 将覆盖 append-to-body
});

const isProgressVariant = computed(() => props.variant === 'progress');

/** progress：高度随内容；可选 height 作为最小高度。standard：height 默认 200px 作 min-height */
const popupBoxStyle = computed(() => {
  const h = props.height;
  if (isProgressVariant.value) {
    if (h === undefined || h === null || h === '') return {};
    const px = typeof h === 'number' ? `${h}px` : String(h);
    return { minHeight: px };
  }
  const fallback = h ?? 200;
  const px = typeof fallback === 'number' ? `${fallback}px` : String(fallback);
  return { minHeight: px };
});

const {
  width,
  title,
  isShowClose,
  isSuspensionHeader,
  isDanger,
  isDark,
  zIndex,
  closeOnClickModal,
  transition,
  appendToBody,
  appendTo,
} = toRefs(props);

const modelValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const closePopup = () => {
  // modelValue.value = false;
  emit('update:modelValue', false);
  emit('close');
};

const closeBtnClick = () => {
  emit('update:modelValue', false);
};

// 监听 currentValue 的变化，发送事件到父组件同步状态
// watch(isShow, (newValue) => {
//     emit("update:modelValue", newValue);
// });
</script>

<template>
  <div class="popup-wrap">
    <!-- <img src="@/assets/images/home/bg_pop.png" alt=""> -->
    <el-dialog
      v-model="modelValue"
      class="custom-popup-dialog"
      :width="width"
      :show-close="false"
      :destroy-on-close="destroyOnClose"
      :z-index="zIndex"
      :close-on-click-modal="isShowClose || closeOnClickModal"
      :close-on-press-escape="false"
      @close="closePopup"
      :top="top"
      :transition="transition"
      :append-to-body="appendToBody"
      :append-to="appendTo"
    >
      <div
        class="popup-box"
        :class="{
          'danger-bg': isDanger,
          'dark-bg': isDark,
          'popup-box--progress': isProgressVariant,
        }"
        :style="popupBoxStyle"
      >
        <template v-if="isProgressVariant">
          <div class="fc-ac progress-tip-box">
            <img
              v-if="progressVisual === 'role'"
              class="gif-img"
              src="@/assets/images/popup/generate.gif"
              alt=""
            />
            <img
              v-else-if="progressVisual === 'cloud'"
              class="cloud-img mb-16"
              src="@/assets/images/popup/icon_cloud.png"
              alt=""
            />
            <img
              v-else-if="progressVisual === 'import'"
              class="import-img mb-16"
              src="@/assets/images/popup/icon_data.png"
              alt=""
            />
            <div class="fs-16 fw-500 mb-21">{{ progressTip }}</div>
            <CustomSlider
              v-if="showProgressBar"
              :model-value="progress"
              :trackHeight="10"
              :isControl="false"
              :trackWidth="252"
              trackColor="rgba(55,133,255,0.3)"
            />
            <div class="progress-popup-footer">
              <slot></slot>
            </div>
          </div>
        </template>
        <template v-else>
          <slot name="header"></slot>
          <div
            class="fr-ac-jsb"
            :class="isSuspensionHeader ? 'suspension' : ''"
          >
            <div class="w-30 h-30"></div>
            <div class="fs-18">{{ title }}</div>
            <div
              v-if="isShowClose"
              class="fr-ac-jc pointer w-30 h-30 mr-1 btn-hover zi-99"
              @click.stop="closeBtnClick"
            >
              <IconFont name="icon_close" :size="12" />
            </div>
            <div v-else></div>
          </div>
          <slot></slot>
        </template>
      </div>
    </el-dialog>
  </div>
</template>

<style lang="scss">
.custom-popup-dialog.el-dialog {
  /* 覆盖 EP 主题变量，避免 append-to-body 后仍用默认白底 */
  --el-dialog-bg-color: var(--bg-color-white);
  padding: 0 !important;
  background: var(--bg-color-white) !important;
  backdrop-filter: blur(20px);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
}

.custom-popup-dialog .el-dialog__header {
  padding: 0 !important;
  margin: 0 !important;
}

.custom-popup-dialog .el-dialog__body {
  padding: 0 !important;
  background: transparent !important;
  color: inherit;
}
</style>

<style lang="scss" scoped>
.popup-box {
  width: 100%;
  min-height: 138px;
  border-radius: var(--border-radius);
  position: relative;
  background: var(--bg-color-white);
  padding: 20px 30px 30px;
  color: var(--text-color);

  .suspension {
    position: absolute;
    top: 20px;
    right: 30px;
  }
}

.btn-hover:hover {
  background: rgba(55, 133, 255, 0.2);
  border-radius: var(--border-radius);
}

.danger-bg {
  /*background: linear-gradient(
    180deg,
    rgba(21, 35, 74, 0.4) 0%,
    rgba(255, 82, 98, 0.4) 100%
  );*/
  background: var(--card-bg-color);
}
.dark-bg {
  background: var(--bg-color);
}

.popup-box--progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0 !important;
  padding: 4px 30px 24px !important;
}

.progress-tip-box {
  position: relative;
  width: 100%;
  align-items: center;
}

/* 进度条下方插槽：避免长文件名 + 下载速度被裁切或与右侧百分比单行挤压 */
.progress-popup-footer {
  width: 100%;
  align-self: stretch;
  box-sizing: border-box;
  padding-bottom: 2px;
}

.progress-popup-footer :deep(.fr-ac-jsb) {
  align-items: flex-start;
  gap: 8px;
}

.progress-popup-footer :deep(.fr-ac-jsb > div:first-of-type) {
  flex: 1;
  min-width: 0;
  white-space: normal;
  word-break: break-all;
  line-height: 1.45;
}

.progress-popup-footer :deep(.fr-ac-jsb > div:last-of-type) {
  flex-shrink: 0;
}

.gif-img {
  height: 120px;
}

.cloud-img {
  width: 128px;
  height: 88px;
}

.import-img {
  width: 112px;
  height: 101px;
}
</style>
